/**
 * Gets used on route 'settings.servicegroups'
 */
import ObjectCopyService from "external-services/object-copy-service/index";
import {first} from "rxjs/operators";
import {sortBy} from "lodash-es";
import ServiceGroupsService from "external-services/service-groups-service/serviceGroups.service";
import PouchActions, {PouchDocType} from 'app/actions/pouch.actions';

const NS = 'ServiceGroupsCtrl';

export default function ServiceGroupsCtrl(
  $rootScope, $scope, $log, $mdDialog, $mdToast, $filter, $timeout, unregister
) {
  "ngInject";


  const vm = this;

  let listeners;
  let highestOrder = 0;
  let saveInterval;

  //-- define public properties
  // states
  vm.LOADING = 1;
  vm.ENTER = 0;
  vm.SAVED = 2;
  vm.ERROR_SAVING = 3;
  vm.ERROR_DELETING = 4;
  vm.ERROR_LOADING = 5;
  //-- define public methods
  vm.trySavingAgain = trySavingAgain;
  vm.tryDeletingAgain = tryDeletingAgain;
  vm.moveUp = moveUp;
  vm.moveDown = moveDown;
  vm.checkIsDisabled = checkIsDisabled;
  vm.addServiceGroup = addServiceGroup;
  vm.editServiceGroup = editServiceGroup;
  vm.deleteServiceGroup = deleteServiceGroup;

  // lifecycle hook
  vm.$onInit = () => {
    vm.state = vm.LOADING;

    //-- listen
    listeners = [
      // $rootScope.$on('pouch-synced-doctype-service-group', _getServiceGroups)
      PouchActions.handlePouchSyncedDoctype(PouchDocType.serviceGroup, () => _getServiceGroups())
    ];

    _getServiceGroups();
  };

  /**
   * Lifecycle hook.
   * Unfortunately can't use vm.$onDestroy because we're using a `controller`, not a `component`.
   * And we can't use `component` with current version of ui-router.
   */
  $scope.$on('$destroy', () => {
    unregister(listeners);
  });

  function _getServiceGroups() {

    ServiceGroupsService.getServiceGroups()
      .pipe(first())
      .subscribe(serviceGroups => {
        /**
         * I dislike using timeout, but according this thread it is better than using $digest or $apply
         * https://stackoverflow.com/questions/12729122/angularjs-prevent-error-digest-already-in-progress-when-calling-scope-apply
         */
        $timeout(() => {
          vm.serviceGroups = sortBy(serviceGroups, 'order');
          _setOrder(vm.serviceGroups);
          vm.state = vm.ENTER;
        });
      }, err => {
        vm.state = vm.ERROR_LOADING;
        $log.error('serviceGroups failed to load', err);
      });
  }

  function moveUp(index) {
    const serviceGroup = vm.serviceGroups[index];
    var aboveItem = vm.serviceGroups[index - 1];
    var aboveOrder = aboveItem.order;
    aboveItem.order = serviceGroup.order;
    serviceGroup.order = aboveOrder;
    vm.serviceGroups = $filter('orderBy')(vm.serviceGroups, 'order');
    saveDelay();
  }

  function moveDown(index) {
    const serviceGroup = vm.serviceGroups[index];
    var belowItem = vm.serviceGroups[index + 1];
    var belowOrder = belowItem.order;
    belowItem.order = serviceGroup.order;
    serviceGroup.order = belowOrder;
    vm.serviceGroups = $filter('orderBy')(vm.serviceGroups, 'order');
    saveDelay();
  }

  function trySavingAgain() {
    _saveServiceGroups();
  }

  function tryDeletingAgain() {
    vm.state = vm.ENTER;
  }

  function saveDelay() {
    vm.waitingToSave = true;
    if (saveInterval) {
      $timeout.cancel(saveInterval);
    }
    saveInterval = $timeout(_saveServiceGroups, 2000);
  }

  function _saveServiceGroups() {
    vm.state = vm.LOADING;
    ServiceGroupsService.saveServiceGroups(vm.serviceGroups)
      .pipe(first())
      .subscribe(() => {
        vm.state = vm.ENTER;
        $mdToast.show(
          $mdToast.simple()
            .content('Service Groups Saved')
            .position('bottom right')
            .theme('saved')
            .hideDelay(3000)
        );
        vm.waitingToSave = false;
      }, err => {
        vm.state = vm.ERROR_SAVING;
        $log.error(NS, 'Saving ServiceGroups error', err);
      });
  }

  function checkIsDisabled(index) {
    return index === (vm.serviceGroups.length-1) || vm.serviceGroups.length === 1;
  }

  function deleteServiceGroup(evt, serviceGroup) {
    var confirm = $mdDialog.confirm()
      .parent(angular.element(document.body))
      .title('Are you sure?')
      .content('Delete service group ' + serviceGroup.name)
      .ariaLabel('Delete service Group')
      .ok('Yes')
      .cancel('No')
      .targetEvent(evt);
    $mdDialog.show(confirm).then(async () => {
      try {
        const response = await ServiceGroupsService.deleteServiceGroup(serviceGroup);
        if (!response.ok) {
          vm.state = vm.ERROR_DELETING;
        }
        console.log(NS, 'response', response);
      } catch (err) {
        vm.state = vm.ERROR_DELETING;
        console.error(NS, 'Deleting ServiceGroups error', err);
      }
    });
  }

  function addServiceGroup(evt) {
    const scope = $rootScope.$new(true);

    $mdDialog.show({
      controller: 'EditServiceGroupCtrl',
      controllerAs: 'vm',
      templateUrl: 'app/settings/serviceGroups/editServiceGroup.html',
      clickOutsideToClose: true,
      bindToController: true,
      locals: {
        serviceGroup: null,
        highestOrder: highestOrder
      },
      targetEvent: evt,
      scope: scope,

      // simulates the lifecycle hooks because mdDialog does call them
      onComplete: () => {
        try {
          scope.$onInit();
        } catch(err) {
          $log.error(NS, 'addServiceGroup onComplete error', err);
        }
      }
    });
  }

  function editServiceGroup(evt, serviceGroup) {
    if (!vm.waitingToSave) {
      const scope = $rootScope.$new(true);
      $mdDialog.show({
        controller: 'EditServiceGroupCtrl',
        controllerAs: 'vm',
        clickOutsideToClose: true,
        bindToController: true,
        locals: {
          serviceGroup: ObjectCopyService.angularSafeCopy(serviceGroup),
          highestOrder: highestOrder
        },
        templateUrl: 'app/settings/serviceGroups/editServiceGroup.html',
        targetEvent: evt,
        scope: scope,

        // simulates the lifecycle hooks because mdDialog does call them
        onComplete: () => {
          try {
            scope.$onInit();
          } catch(err) {
            $log.error(NS, 'editServiceGroup onComplete error', err);
          }
        }
      });
    }
  }

  function _setOrder(serviceGroups) {
    var lastOrder = 0;
    serviceGroups.forEach(item => {
      if (!isNaN(item.order)) {
        if (item.order === lastOrder) {
          item.order = lastOrder += 1;
        }
      } else {
        item.order = lastOrder += 1;
      }
      lastOrder = item.order;
    });

    highestOrder = lastOrder;
  }
}
